'use client';

import { useState } from 'react';
import Card from '@/components/ui/Card';
import Spinner from '@/components/ui/Spinner';
import ErrorAlert from '@/components/ui/ErrorAlert';
import ESM2to1FilterForm from '@/components/filters/ESM2to1FilterForm';
import ESM2to1PredictionTable from '@/components/esm2to1/ESM2to1PredictionTable';
import { useESM2to1FilterOptions } from '@/hooks/useESM2to1FilterOptions';
import { useESM2to1Predictions } from '@/hooks/useESM2to1Predictions';
import BackButton from '@/components/ui/BackButton';
import { Search, Target } from 'lucide-react';
import PageLayout from '@/components/layout/PageLayout';

export default function ESM2to1PredictionPage() {
  const { filterOptions, loading: loadingFilters, error: filterError } = useESM2to1FilterOptions();
  const { predictions, loading: loadingPredictions, error: predictionsError, fetchESM2to1Predictions } = useESM2to1Predictions();
  const [hasSearched, setHasSearched] = useState(false);

  const handleFilterSubmit = async (filters: Record<string, unknown>) => {
    await fetchESM2to1Predictions(filters);
    setHasSearched(true);
  };

  const error = filterError || predictionsError;

  if (loadingFilters) {
    return (
      <PageLayout>
        <div className="flex justify-center items-center min-h-96">
          <Spinner size="lg" className="text-emerald-600" />
        </div>
      </PageLayout>
    );
  }

  return (
    <PageLayout>
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 py-8 md:py-10 space-y-6">
        <header className="mb-8 flex items-start gap-3">
          <div className="md:mt-0.5">
            <BackButton href="/esm-stock-analysis" />
          </div>
          <div>
            <h1 className="text-2xl md:text-3xl font-bold tracking-tight text-zinc-900 dark:text-white">
              ESM 2-to-1 Exit Predictions
            </h1>
            <p className="mt-1 text-sm md:text-base text-zinc-500 dark:text-zinc-400">
              Predicted exit dates for stocks transitioning from ESM Stage 2 to Stage 1
            </p>
          </div>
        </header>

        {error && (
          <ErrorAlert 
            message={error}
            className="mb-6"
          />
        )}

        {/* Search Form */}
        <Card className="p-0">
          <div className="flex items-center gap-2 border-b border-zinc-200 dark:border-zinc-800 px-6 py-4">
            <Search className="h-4 w-4 text-emerald-600 dark:text-emerald-400" />
            <h2 className="text-base font-semibold text-zinc-900 dark:text-white">Filter Stocks</h2>
          </div>
          {filterOptions && (
            <ESM2to1FilterForm
              filterOptions={filterOptions}
              onSubmit={handleFilterSubmit}
              isLoading={loadingPredictions}
            />
          )}
        </Card>

        {/* Results */}
        {loadingPredictions && (
          <Card className="p-8 text-center">
            <Spinner size="lg" className="mx-auto mb-4 text-emerald-600" />
            <p className="text-zinc-500 dark:text-zinc-400">Searching ESM 2-to-1 predictions...</p>
          </Card>
        )}

        {!loadingPredictions && hasSearched && predictions.length > 0 && (
          <ESM2to1PredictionTable predictions={predictions} />
        )}

        {!loadingPredictions && hasSearched && predictions.length === 0 && (
          <Card className="p-8 text-center">
            <div className="flex flex-col items-center">
              <div className="w-16 h-16 bg-zinc-100 dark:bg-zinc-800 rounded-full flex items-center justify-center mb-4">
                <Search className="h-8 w-8 text-zinc-400" />
              </div>
              <h3 className="text-lg font-medium text-zinc-900 dark:text-white mb-2">No predictions found</h3>
              <p className="text-zinc-500 dark:text-zinc-400 text-center max-w-md">
                Try adjusting your search criteria or check back later for updated predictions.
              </p>
            </div>
          </Card>
        )}


        {/* ESM Algorithm Explanation */}
        <Card>
          <div className="flex items-start gap-3 mb-6">
            <Target className="h-5 w-5 text-emerald-600 dark:text-emerald-400 flex-shrink-0 mt-0.5" />
            <div>
              <h3 className="text-base font-semibold text-zinc-900 dark:text-white mb-2">ESM Stage 2 to Stage 1 Exit Algorithm</h3>
              <p className="text-zinc-700 dark:text-zinc-300 text-sm leading-relaxed">
                Algorithm-based predictions using official ESM exit rules effective July 28, 2025. Uses dateutil libraries for precise calendar calculations.
              </p>
            </div>
          </div>

          <div className="space-y-6">
            {/* Step 1 - Minimum Retention */}
            <div className="bg-zinc-50 dark:bg-zinc-800/60 rounded-xl p-4 border border-zinc-200 dark:border-zinc-800">
              <div className="flex items-center gap-3 mb-3">
                <div className="w-8 h-8 bg-emerald-500/10 text-emerald-700 dark:text-emerald-400 rounded-full flex items-center justify-center font-bold text-sm">1</div>
                <h4 className="text-md font-semibold text-zinc-900 dark:text-white">Minimum Retention Period</h4>
              </div>
              <div className="ml-11 text-sm text-zinc-700 dark:text-zinc-300 space-y-2">
                <p>• Stock must complete exactly <strong>1 calendar month</strong> in Stage 2 before exit eligibility</p>
                <p>• Eligibility starts on <strong>1 month + 1 day</strong> after entry date</p>
                <p className="text-xs bg-white dark:bg-zinc-900 border border-zinc-200 dark:border-zinc-800 p-2 rounded-lg">
                  <strong>Example:</strong> Entry on August 1, 2025 → Eligible from September 2, 2025
                </p>
              </div>
            </div>

            {/* Step 2 - Weekly Review */}
            <div className="bg-zinc-50 dark:bg-zinc-800/60 rounded-xl p-4 border border-zinc-200 dark:border-zinc-800">
              <div className="flex items-center gap-3 mb-3">
                <div className="w-8 h-8 bg-emerald-500/10 text-emerald-700 dark:text-emerald-400 rounded-full flex items-center justify-center font-bold text-sm">2</div>
                <h4 className="text-md font-semibold text-zinc-900 dark:text-white">Weekly Review Process</h4>
              </div>
              <div className="ml-11 text-sm text-zinc-700 dark:text-zinc-300 space-y-2">
                <p>• Reviews conducted weekly by exchanges on <strong>Fridays</strong></p>
                <p>• If Friday is a market holiday, check <strong>Thursday</strong> instead</p>
                <p>• Exit effective from review day where conditions are met</p>
                <p className="text-xs bg-white dark:bg-zinc-900 border border-zinc-200 dark:border-zinc-800 p-2 rounded-lg">
                  <strong>2025 Holidays:</strong> Oct 2 (Thu), Oct 21 (Tue), Oct 22 (Wed), Nov 5 (Wed), Dec 25 (Thu)
                </p>
              </div>
            </div>

            {/* Step 3 - Price Variation */}
            <div className="bg-zinc-50 dark:bg-zinc-800/60 rounded-xl p-4 border border-zinc-200 dark:border-zinc-800">
              <div className="flex items-center gap-3 mb-3">
                <div className="w-8 h-8 bg-emerald-500/10 text-emerald-700 dark:text-emerald-400 rounded-full flex items-center justify-center font-bold text-sm">3</div>
                <h4 className="text-md font-semibold text-zinc-900 dark:text-white">Price Variation Condition</h4>
              </div>
              <div className="ml-11 text-sm text-zinc-700 dark:text-zinc-300 space-y-2">
                <p>• Calculate close-to-close price variation over <strong>past 1 calendar month</strong> from review date</p>
                <p>• Exit eligible if: <strong>Review High Price &lt; Past High Price × 1.15</strong></p>
                <p>• If price has risen <strong>15% or more</strong> in past month → stays in Stage 2</p>
                <p>• If risen <strong>less than 15%</strong> (or fallen) → can exit to Stage 1</p>
              </div>
            </div>

            {/* Step 4 - Forward Prediction */}
            <div className="bg-zinc-50 dark:bg-zinc-800/60 rounded-xl p-4 border border-zinc-200 dark:border-zinc-800">
              <div className="flex items-center gap-3 mb-3">
                <div className="w-8 h-8 bg-emerald-500/10 text-emerald-700 dark:text-emerald-400 rounded-full flex items-center justify-center font-bold text-sm">4</div>
                <h4 className="text-md font-semibold text-zinc-900 dark:text-white">Prediction Algorithm</h4>
              </div>
              <div className="ml-11 text-sm text-zinc-700 dark:text-zinc-300 space-y-2">
                <p>• Count trading days remaining until next review date</p>
                <p>• Assume maximum daily drop: <strong>2% per trading day</strong> (Stage 2 circuit limit)</p>
                <p>• Calculate: <strong>Min Possible Price = Current Price × (0.98 ^ Trading Days)</strong></p>
                <p>• If Min Possible Price &lt; Target (Past High Price × 1.15) → <strong>Exit Possible</strong></p>
                <p>• If not possible this week → check next week&apos;s review with new 1-month-back reference</p>
                <p className="text-xs bg-white dark:bg-zinc-900 border border-zinc-200 dark:border-zinc-800 p-2 rounded-lg">
                  <strong>Implementation:</strong> Uses dateutil.relativedelta for precise month calculations across varying month lengths and leap years
                </p>
              </div>
            </div>

            {/* Technical Notes */}
            <div className="bg-zinc-50 dark:bg-zinc-800/60 rounded-xl p-4 border border-zinc-200 dark:border-zinc-800">
              <h4 className="text-sm font-semibold text-zinc-900 dark:text-white mb-2">Technical Implementation Notes</h4>
              <div className="text-xs text-zinc-500 dark:text-zinc-400 space-y-1">
                <p>• All prices are corporate action-adjusted close prices</p>
                <p>• Excludes weekends and market holidays when counting trading days</p>
                <p>• Algorithms iterate up to 4-5 weeks maximum to find earliest possible exit</p>
                <p>• No exit guarantee if conditions never met (stock stays Stage 2 indefinitely)</p>
              </div>
            </div>
          </div>
        </Card>
      </div>
    </PageLayout>
  );
}
